import { useState } from 'react'
import { PRODUCT_VIDEOS } from './productVideos'
import ProductCard from '@/components/molecules/ProductCard'
import ProductModal from './ProductModal'
import buildLogo from '@/assets/illustrations/unimobuild logo.svg'
import liveLogo from '@/assets/illustrations/unimolive logo.svg'
import reportLogo from '@/assets/illustrations/unimioreportlogo.svg'

// Copy source: content/unimo-messaging.md, "Product descriptions". CTA
// destinations per instruction: unimoBuild is self-serve (Buy unimo →
// /pricing-new); unimoLive and unimoReport are enterprise (Talk to sales →
// /contact-new), matching the labelling/variant convention already
// established on PricingPage.tsx.
const PRODUCTS = [
    {
        key: 'build',
        logo: buildLogo,
        alt: 'unimoBuild',
        tagline: 'Turn any site document into a living 3D model - in minutes.',
        description:
            'unimoBuild is an AI-driven digital twin tool that creates accurate, living 3D models in minutes. Import PDFs, screenshots, photos, IFC exports from Navisworks or Revit and just ask it what you need. No specialist team or lengthy training needed. Install it, open it, start building.',
        ctaLabel: 'Contact us',
        ctaHref: '/contact?topic=pricing',
        ctaVariant: 'solid' as const,
    },
    {
        key: 'live',
        logo: liveLogo,
        alt: 'unimoLive',
        tagline: 'Everything happening on your site, visible in real time.',
        description:
            'unimoLive is out of the box compatible with almost every sensor on the planet. It monitors weather, noise, power, and any other data your site produces. One view of everything, updating as it happens. When something changes on site, you can intervene before it becomes a bigger problem.',
        ctaLabel: 'Talk to sales',
        ctaHref: '/contact',
        ctaVariant: 'ghost' as const,
    },
    {
        key: 'report',
        logo: reportLogo,
        alt: 'unimoReport',
        tagline: 'No IT tickets. Accurate dashboards, in seconds.',
        description:
            'unimoReport makes your data work for you, turning complex datasets into intelligence that is visualised, actionable, and ready to use. Pull in your unimoLive data or connect your own from tools like Microsoft Fabric. Build custom dashboards and stay compliant with on-demand reporting.',
        ctaLabel: 'Talk to sales',
        ctaHref: '/contact',
        ctaVariant: 'ghost' as const,
    },
]

const CONSTRUCTION_EXAMPLES: Record<string, string> = {
    build: 'Turn your construction site plans into a living 3D model.',
    live: 'See noise, dust and air quality across your construction site in real time.',
    report: 'Turn site monitoring data into clear reports for your team and clients.',
}

interface ProductsSectionProps {
    variant?: 'default' | 'construction';
    heading?: string;
}

export function ProductsSection({ variant = 'default', heading = 'Explore our products' }: ProductsSectionProps) {
    const [openKey, setOpenKey] = useState<string | null>(null)
    const products = PRODUCTS.map((product) => variant === 'construction' ? {
        ...product,
        tagline: CONSTRUCTION_EXAMPLES[product.key],
        videoKey: `construction-${product.key}`,
        ctaLabel: 'Meet us one-to-one',
        ctaHref: '#book-a-meeting',
    } : { ...product, videoKey: product.key })

    const openProduct = products.find((product) => product.key === openKey) ?? null

    return (
        <section className="pt-section-headline-gap-top">
            <h2 className="mb-section-headline-gap-bottom text-2xl font-medium text-unimo-slate lg:text-4xl">
                {heading}
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {products.map((product) => (
                    <div key={product.key} data-testid={`product-card-${product.key}`} className="min-w-0">
                        <ProductCard
                            logoSrc={product.logo}
                            logoAlt={product.alt}
                            tagline={product.tagline}
                            mediaKind={product.key==='live'?'monitoring':product.key==='report'?'reporting':undefined}
                            mediaLabel="Video — coming soon"
                            animatedPreviewSrc={PRODUCT_VIDEOS[product.videoKey]?.animatedPreviewSrc}
                            videoSrc={PRODUCT_VIDEOS[product.videoKey]?.previewSrc}
                            posterSrc={PRODUCT_VIDEOS[product.videoKey]?.posterSrc}
                            previewEnabled={openKey === null}
                            onOpen={() => setOpenKey(product.key)}
                        />
                    </div>
                ))}
            </div>

            {openProduct && <ProductModal product={openProduct} onClose={() => setOpenKey(null)} />}
        </section>
    )
}

export default ProductsSection
